import React, { useState } from 'react';

/** OR semantics: show images that have at least one of the selected classifications. */
export const DvcClassificationFilterBar: React.FC<{
  available: string[];
  selected: string[];
  onChange: (next: string[]) => void;
  loading: boolean;
  error?: string;
}> = ({ available, selected, onChange, loading, error }) => {
  const [input, setInput] = useState('');

  const suggestions = available.filter((a) => !selected.includes(a));
  const add = (name: string) => {
    const t = String(name).trim();
    if (!t || selected.includes(t)) return;
    onChange([...selected, t]);
    setInput('');
  };

  return (
    <div
      style={{
        marginBottom: '1rem',
        padding: '0.75rem',
        border: '1px solid #dee2e6',
        borderRadius: '6px',
        background: '#fafafa',
      }}
    >
      <div style={{ fontSize: '0.85rem', color: '#444', marginBottom: '0.35rem' }}>
        Filter by classification (image must have <strong>any</strong> of the selected labels)
      </div>
      {loading && (
        <div style={{ fontSize: '0.85rem', color: '#666' }}>Loading classification labels…</div>
      )}
      {error && !loading && (
        <div style={{ fontSize: '0.85rem', color: '#b02a37', marginBottom: '0.35rem' }}>{error}</div>
      )}
      {!loading && !error && available.length === 0 && (
        <div style={{ fontSize: '0.85rem', color: '#666' }}>
          No classification labels found in this dataset (CSV / ingestion).
        </div>
      )}
      {!loading && available.length > 0 && (
        <>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: '0.35rem',
              padding: '0.4rem',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              background: '#fff',
              marginBottom: '0.35rem',
            }}
          >
            {selected.map((tag) => (
              <span
                key={tag}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.2rem',
                  padding: '0.2rem 0.45rem',
                  fontSize: '0.85rem',
                  background: '#e7f1ff',
                  border: '1px solid #b6d4fe',
                  borderRadius: '4px',
                }}
              >
                {tag}
                <button
                  type="button"
                  onClick={() => onChange(selected.filter((x) => x !== tag))}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    lineHeight: 1,
                    color: '#666',
                  }}
                  aria-label={`Remove ${tag}`}
                >
                  ×
                </button>
              </span>
            ))}
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  const m = suggestions.find(
                    (s) => s.toLowerCase() === input.trim().toLowerCase()
                  );
                  if (m) add(m);
                  else if (input.trim() && available.includes(input.trim())) add(input.trim());
                }
              }}
              list="dvc-classification-filter-datalist"
              placeholder="Type or pick a classification…"
              style={{
                border: 'none',
                outline: 'none',
                padding: '0.2rem',
                fontSize: '0.9rem',
                minWidth: '160px',
                flex: '1 1 140px',
              }}
              disabled={!!error}
            />
            <datalist id="dvc-classification-filter-datalist">
              {suggestions.slice(0, 200).map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', alignItems: 'center' }}>
            <span style={{ fontSize: '0.8rem', color: '#666' }}>Quick add:</span>
            {suggestions.length === 0 ? (
              <span style={{ fontSize: '0.8rem', color: '#999' }}>
                {selected.length > 0 ? 'All matching labels selected' : 'No labels'}
              </span>
            ) : (
              suggestions.slice(0, 24).map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => add(tag)}
                  style={{
                    padding: '0.2rem 0.45rem',
                    fontSize: '0.8rem',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    background: '#fff',
                    cursor: 'pointer',
                  }}
                >
                  {tag}
                </button>
              ))
            )}
            {selected.length > 0 && (
              <button
                type="button"
                onClick={() => onChange([])}
                style={{
                  marginLeft: '0.5rem',
                  padding: '0.25rem 0.5rem',
                  fontSize: '0.8rem',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  background: '#fff',
                  cursor: 'pointer',
                }}
              >
                Clear filters
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};
